import { Link } from "@tanstack/react-router";
import { Phone, MapPin, Star, ShieldCheck, BadgeCheck } from "lucide-react";
import logo from "@/assets/mckay-logo.png.asset.json";
import { company } from "@/data/site";
import { roofingTiles, solarTiles } from "@/data/serviceTiles";

export function SiteFooter() {
  return (
    <footer className="bg-primary text-primary-foreground">
      <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-5">
          {/* Brand */}
          <div>
            <img src={logo.url} alt="McKay Roofing & Solar" className="h-auto w-full max-w-[180px]" />
            <div className="mt-6">
              <div className="text-base font-bold text-white">Licensed &amp; Insured</div>
              <div className="mt-1 text-sm text-primary-foreground/80">License #1130793</div>
            </div>
            <div className="mt-5 flex items-center gap-1 text-accent">
              {[1,2,3,4,5].map((i) => <Star key={i} className="h-4 w-4 fill-current" />)}
              <span className="ml-2 text-xs text-primary-foreground/70">{company.rating} · {company.reviewCount}+ reviews</span>
            </div>
          </div>

          {/* Explore */}
          <div>
            <div className="mb-3 text-base font-bold text-white">Explore</div>
            <ul className="space-y-2 text-sm text-primary-foreground/90">
              <li><Link to="/" className="hover:text-white">Home</Link></li>
              <li><Link to="/about" className="hover:text-white">About</Link></li>
              <li><Link to="/why-choose-us" className="hover:text-white">Why Choose Us</Link></li>
              <li><Link to="/areas-served" className="hover:text-white">Service Area</Link></li>
              <li><Link to="/roofing-materials" className="hover:text-white">Roofing Materials</Link></li>
              <li><Link to="/utility-companies" className="hover:text-white">Utility Companies</Link></li>
              <li><Link to="/blog" className="hover:text-white">Blog</Link></li>
              <li><Link to="/faq" className="hover:text-white">FAQ</Link></li>
              <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
            </ul>
          </div>

          {/* Roofing Services */}
          <div>
            <div className="mb-3 text-base font-bold text-white">Roofing Services</div>
            <ul className="space-y-2 text-sm text-primary-foreground/90">
              {roofingTiles.map((t) => (
                <li key={t.slug}><Link to={t.href} className="hover:text-white">{t.title}</Link></li>
              ))}
            </ul>
          </div>

          {/* Solar Services */}
          <div>
            <div className="mb-3 text-base font-bold text-white">Solar Services</div>
            <ul className="space-y-2 text-sm text-primary-foreground/90">
              {solarTiles.map((t) => (
                <li key={t.slug}><Link to={t.href} className="hover:text-white">{t.title}</Link></li>
              ))}
              <li><Link to="/book/solar-repair" className="font-semibold text-white hover:text-primary-foreground/80">Book Solar Repair</Link></li>
            </ul>
          </div>

          {/* Contact Information */}
          <div>
            <div className="mb-3 text-base font-bold text-white">Contact Information</div>
            <ul className="space-y-3 text-sm text-primary-foreground/90">
              <li>
                <div className="text-base font-bold text-white">Southern California Office</div>
                <div className="mt-2 flex items-start gap-2">
                  <MapPin className="mt-0.5 h-4 w-4 text-white" />
                  <span>9520 Padgett St, Suite 107,<br />San Diego, CA 92126</span>
                </div>
              </li>
              <li>
                <a href={company.phoneHref} className="flex items-center gap-2 text-white hover:text-primary-foreground/80">
                  <Phone className="h-4 w-4" />{company.phone}
                </a>
              </li>
              <li>
                <a href={`mailto:${company.email}`} className="flex items-center gap-2 text-white hover:text-primary-foreground/80 break-all">
                  <span aria-hidden>✉</span>{company.email}
                </a>
              </li>
            </ul>
            <div className="mt-5">
              <div className="text-base font-bold text-white">Available Hours</div>
              <div className="mt-1 text-sm leading-relaxed text-primary-foreground/80">
                Mon – Fri: 7:00 AM – 4:30 PM<br />
                Sat: Closed<br />
                Sun: Closed
              </div>
            </div>
          </div>
        </div>

        {/* Credentials bar */}
        <div className="mt-10 grid gap-4 rounded-2xl border border-primary-foreground/10 bg-primary-foreground/5 p-5 sm:grid-cols-2 lg:grid-cols-3">
          <a
            href="https://www.nrca.net/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 text-primary-foreground/90 transition-colors hover:text-white"
          >
            <BadgeCheck className="h-6 w-6 shrink-0 text-accent" />
            <div>
              <div className="text-sm font-bold">NRCA Member</div>
              <div className="text-xs text-primary-foreground/70">National Roofing Contractors Association</div>
            </div>
          </a>
          <div className="flex items-center gap-3 text-primary-foreground/90">
            <ShieldCheck className="h-6 w-6 shrink-0 text-accent" />
            <div>
              <div className="text-sm font-bold">CSLB #1130793</div>
              <div className="text-xs text-primary-foreground/70">Licensed California contractor</div>
            </div>
          </div>
          <div className="flex items-center gap-3 text-primary-foreground/90">
            <BadgeCheck className="h-6 w-6 shrink-0 text-accent" />
            <div>
              <div className="text-sm font-bold">Manufacturer Systems</div>
              <div className="text-xs text-primary-foreground/70">Owens Corning, IKO, Enphase, Tesla &amp; more</div>
            </div>
          </div>
        </div>

        <div className="mt-10 flex flex-col gap-3 border-t border-primary-foreground/15 pt-6 text-xs text-primary-foreground/80 sm:flex-row sm:items-center sm:justify-between">
          <div>© Nuon Energy LLC, DBA McKay Roofing and Solar {new Date().getFullYear()}</div>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <Link to="/terms" className="hover:text-white">Terms &amp; Conditions</Link>
            <Link to="/privacy" className="hover:text-white">Privacy Policy</Link>
            <Link to="/sitemap" className="hover:text-white">Sitemap</Link>
            <Link to="/sms-compliance" className="hover:text-white">SMS Compliance</Link>
            <Link to="/ada-compliance" className="hover:text-white">ADA &amp; Accessibility</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
